import {
  cyan,
  DropdownSelect,
  FaroText,
  FilterMenuOptionType,
  grey,
  NoTranslate,
  Option,
  TagTypes,
  TextField,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { Box, Stack } from "@mui/material";
import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { dropDownStyle } from "./annotation-editor-meta-fields";

/** The props of the menu content */
export type SearchableDropdownProps = {
  /** Current value of the dropdown field. */
  selectedOption?: string;

  /** List of available option to select in the dropdown field.   */
  options: Option[];

  /** Optional label on top of the control */
  label?: string;

  /** Optional tag added to the top label. If they label is not defined, the tag is not visible */
  tag?: TagTypes;

  /** Function called when the selected value in dropdown field changes */
  onOptionChange(option?: string): void;

  /** If true, the component is disabled */
  disabled?: boolean;

  /** if true make control box red and show default error message below control  */
  error?: boolean;

  /** Callback executed when the dropdown is closed */
  onClose?(): void;
};

/**
 * @returns The content of searchable dropdown.
 *
 * Note: in current implementation list of option will be not translated
 */
export function SearchableDropdown({
  selectedOption,
  options,
  label,
  tag,
  error,
  onOptionChange,
  disabled,
  onClose,
}: SearchableDropdownProps): JSX.Element {
  const [currentOption, setCurrentOption] = useState<
    SearchableListContentProps | undefined
  >();

  const searchOptions = useMemo(
    () =>
      options.map((o) => ({
        ...o,
        label: <NoTranslate>{o.label}</NoTranslate>,
        searchText: isValidElement<{ children: string }>(o.label)
          ? o.label.props.children
          : "",
      })),
    [options],
  );

  useEffect(() => {
    // This component is used for both creation and editing of annotation
    // At opening component selectedOption is the one obtained from saved annotation.
    // It could be option, which not present in current list of available options
    // We need to search for selectedOption in currentOptions, allowing it could be not found.
    const option = options.find((o) => o.key === selectedOption);
    if (option?.key !== currentOption?.key) {
      setCurrentOption(option);
    }
  }, [selectedOption, options, currentOption?.key]);

  const updateSelection = useCallback(
    (option: FilterMenuOptionType) => {
      if (option.key !== selectedOption) {
        setCurrentOption(option);
        onOptionChange(option.value.length ? option.key : undefined);
      }
    },
    [onOptionChange, selectedOption],
  );

  return (
    <DropdownSelect
      value={currentOption?.value}
      dark
      error={error}
      disabled={disabled}
      label={label}
      tag={tag}
      sx={dropDownStyle}
      onClose={onClose}
      renderValue={() => (
        <FaroText
          variant="bodyM"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            fontStyle: currentOption?.value ? "normal" : "italic",
            color: currentOption?.value ? grey[100] : grey[500],
          }}
        >
          {currentOption?.label ?? "Select option"}
        </FaroText>
      )}
    >
      <SearchableListContent
        disabled={disabled}
        optionKey={currentOption?.key}
        onSelectOption={updateSelection}
        options={searchOptions}
      />
    </DropdownSelect>
  );
}

export type SearchableListContentProps = FilterMenuOptionType & {
  /** contains content of the string used for search */
  searchText?: string;
};

/** The props of the menu content */
type SearchFilterMenuContentProps = {
  /** rey associated with option  */
  optionKey?: GUID;

  /** Options to be shown in the filter menu */
  options: SearchableListContentProps[];

  /** If true, the component is disabled */
  disabled?: boolean;

  /** Function called when the option selected */
  onSelectOption(option: FilterMenuOptionType): void;
};

/**
 * @returns The content of a searchable menu
 */
export function SearchableListContent({
  options,
  disabled,
  optionKey,
  onSelectOption,
}: SearchFilterMenuContentProps): JSX.Element {
  const [searchText, setSearchText] = useState("");

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (o) =>
          !searchText.length ||
          o.searchText
            ?.toLocaleLowerCase()
            .includes(searchText.toLocaleLowerCase()),
      ),
    [searchText, options],
  );

  return (
    <Stack
      direction="column"
      sx={{ flexGrow: "1", p: 0.5, color: grey[100], width: "480px" }}
    >
      <TextField
        disabled={disabled}
        text={searchText}
        onTextChanged={setSearchText}
        placeholder="Search"
        sx={{ height: 38, mt: 2, py: 0.5 }}
        dark
        fullWidth
      />
      <Box
        component="div"
        sx={{
          mt: 1,
          maxHeight: "50vh",
          minHeight: "5vh",
          overflowY: "auto",
        }}
      >
        {filteredOptions.map((option) => (
          <SelectableMenuOption
            option={option}
            selected={option.key === optionKey}
            onOptionSelected={onSelectOption}
            key={option.key}
          />
        ))}
        {!filteredOptions.length && (
          <FaroText
            variant="bodyM"
            color="inherit"
            sx={{
              px: 2,
              py: 0.75,
            }}
          >
            No results found
          </FaroText>
        )}
      </Box>
    </Stack>
  );
}

type SelectableMenuOptionProps = {
  /** Option to be displayed */
  option: FilterMenuOptionType;

  /** True if the option is selected */
  selected: boolean;

  /** Function called when the option selected */
  onOptionSelected(option: FilterMenuOptionType): void;
};

/**
 * @returns renders single entry for option in searchable menu
 */
function SelectableMenuOption({
  option,
  selected,
  onOptionSelected,
}: SelectableMenuOptionProps): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 0.5,
        cursor: "pointer",
        "&:hover": {
          color: cyan[400],
          backgroundColor: grey[900],
        },
        alignItems: "center",
      }}
      onClick={() => {
        onOptionSelected(option);
      }}
    >
      <TruncatedFaroText
        variant="bodyM"
        sx={{ padding: 1, alignContent: "center" }}
        color={selected ? cyan[400] : "inherit"}
      >
        {option.label}
      </TruncatedFaroText>
    </Stack>
  );
}
